import { Box, useMediaQuery } from '@mui/material';
import { type ReactNode, useEffect } from 'react';

import {
  SIDE_PANEL_DEFAULT_OVERLAY_BREAKPOINT_PX,
  SIDE_PANEL_DEFAULT_WIDTH,
} from '../constants';
import useStyles from './SidePanel.styles';

/**
 * Props for the generic `SidePanel` primitive.
 *
 * The primitive has ZERO product knowledge — all branding (title,
 * translated strings, header actions, chat body, etc.) comes from the
 * consumer via the `header` slot and `children`.
 */
export type SidePanelProps = {
  /** Whether the panel is visible. */
  open: boolean;
  /** Optional class appended to the panel surface. */
  className?: string;
  /**
   * Fired when the user dismisses the panel via scrim click (overlay
   * mode only) or the Escape key (overlay mode only). The consumer
   * owns all open/close state.
   */
  onClose: () => void;
  /** Body content of the panel (rendered below the optional header). */
  children: ReactNode;
  /**
   * Optional header slot rendered at the top of the panel. Consumers
   * supply their own title, icons, and localized aria-labels.
   */
  header?: ReactNode;
  /**
   * Fixed panel width (px) at or above the `sm` breakpoint. Below
   * `sm` the panel always spans 100% of the viewport width.
   * @default SIDE_PANEL_DEFAULT_WIDTH
   */
  width?: number;
  /**
   * Top offset (px) applied to the panel + scrim. Use this to clear a
   * host app's fixed top nav without the primitive needing to know
   * about it.
   * @default 0
   */
  topOffsetPx?: number;
  /**
   * Viewport width (px) at which the panel flips between persistent
   * push mode (≥ this value) and temporary overlay mode (< this value).
   * The primitive subtracts `0.05px` internally when building its
   * `(max-width: ...)` media query so a viewport at exactly this value
   * cannot match both push AND overlay at once.
   * @default SIDE_PANEL_DEFAULT_OVERLAY_BREAKPOINT_PX
   */
  overlayBelowPx?: number;
  /** Accessible label for the `<aside>` landmark. */
  ariaLabel?: string;
  /**
   * Accessible label applied to the scrim's `<button>` element so
   * keyboard and screen-reader users have a localized name for the
   * dismiss affordance. The scrim is a semantic `button` so Enter and
   * Space fire `onClose` natively.
   * @default 'Close panel'
   */
  scrimAriaLabel?: string;
  /**
   * When `true`, the Escape key does NOT close the panel in overlay
   * mode. Use for confirmation flows where accidental dismissal must
   * be prevented.
   * @default false
   */
  disableEscapeKeyDown?: boolean;
  /**
   * Arbitrary `data-*` attributes forwarded onto the `<aside>` element.
   * Lets branded wrappers attach product-specific markers (for example,
   * `data-in-csp`) without the generic primitive knowing about them.
   *
   * The key type is narrowed to `` `data-${string}` `` so non-`data-*`
   * attributes (`id`, `role`, `aria-*`, etc.) are rejected at compile
   * time — consumers needing those should use the primitive's typed
   * props instead of smuggling via this surface.
   *
   * Spread BEFORE the primitive's own internal markers
   * (`data-testid`, `data-state`, `data-variant`, `aria-hidden`,
   * `aria-label`) so consumer attributes can never override the
   * internal contract that tests and styles depend on, even for the
   * `data-*` keys that the type does allow.
   */
  dataAttributes?: Partial<Record<`data-${string}`, string>>;
};

/**
 * Right-side push/overlay panel primitive.
 *
 * Renders a fixed-position `<aside>` styled through `tss-react`
 * `makeStyles`. `@mui/material/Drawer` is deliberately avoided — its
 * Modal + Slide layer fights responsive persistent/overlay variants and
 * has been observed to break under Module Federation when the active
 * theme context is a host app's theme rather than the BD theme.
 *
 * Responsive behavior:
 * - `≥ overlayBelowPx`: persistent panel at `zIndex.drawer`; no scrim;
 *   consumer's `SidePanelLayout` (or equivalent) yields `width` of
 *   margin-right so the panel appears to push content.
 * - `< overlayBelowPx`: temporary panel at `zIndex.modal`; the scrim is
 *   rendered as a semantic `<button>` so keyboard users can Tab to it
 *   and dismiss via Enter/Space natively (mouse users click). When
 *   `open` is false, the scrim stays mounted for the fade-out
 *   transition but is removed from the focus order (`tabIndex={-1}`)
 *   and a11y tree (`aria-hidden`) so it cannot be reached. Escape
 *   also closes the panel unless `disableEscapeKeyDown` is set.
 *
 * The scrim is conditionally rendered (not CSS-hidden) so the a11y
 * tree and hit-test behavior match between modes without extra CSS.
 */
function SidePanel({
  open,
  className,
  onClose,
  children,
  header,
  width = SIDE_PANEL_DEFAULT_WIDTH,
  topOffsetPx = 0,
  overlayBelowPx = SIDE_PANEL_DEFAULT_OVERLAY_BREAKPOINT_PX,
  ariaLabel,
  scrimAriaLabel = 'Close panel',
  disableEscapeKeyDown = false,
  dataAttributes,
}: SidePanelProps) {
  // Internal overlay query subtracts 0.05px so a viewport at exactly
  // `overlayBelowPx` cannot match both this `(max-width)` query and the
  // consumer's `(min-width: ${overlayBelowPx}px)` push query. Mirrors
  // MUI's own `breakpoints.down(...)` generator.
  const overlayMediaQuery = `(max-width:${overlayBelowPx - 0.05}px)`;
  const isOverlay = useMediaQuery(overlayMediaQuery);
  const { classes, cx } = useStyles({
    panelOpen: open,
    isOverlay,
    topOffsetPx,
    width,
  });

  useEffect(() => {
    if (!open || !isOverlay || disableEscapeKeyDown) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, isOverlay, disableEscapeKeyDown, onClose]);

  return (
    <>
      {isOverlay ? (
        <button
          type="button"
          className={classes.scrim}
          data-testid="side-panel-scrim"
          onClick={onClose}
          aria-label={scrimAriaLabel}
          aria-hidden={!open}
          tabIndex={open ? 0 : -1}
        />
      ) : null}
      <aside
        {...dataAttributes}
        className={cx(classes.panelSurface, className)}
        data-testid="side-panel"
        data-state={open ? 'open' : 'closed'}
        data-variant={isOverlay ? 'temporary' : 'persistent'}
        aria-hidden={!open}
        aria-label={ariaLabel}
      >
        {header}
        <Box className={classes.body}>{children}</Box>
      </aside>
    </>
  );
}

export default SidePanel;
